// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://tidy-tasker-api.test/api';

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'tidy_tasker_auth_token',
  USER_DATA: 'tidy_tasker_user_data',
  THEME_MODE: 'tidy_tasker_theme_mode',
} as const;

// Route Paths
export const ROUTES = {
  BRANDING: '/',
  LOGIN: '/admin/login',
  REGISTER: '/admin/register',
  VERIFY_EMAIL: '/admin/verify-email/:token',
  FORGOT_PASSWORD: '/admin/forgot-password',
  RESET_PASSWORD: '/admin/reset-password',
  DASHBOARD: '/admin/dashboard',
  COMPANIES: '/admin/companies',
  STAFF: '/admin/users',
  USERS: '/admin/users',
  EMPLOYEES: '/admin/users/employees',
  CUSTOMERS: '/admin/customers',
  JOBS: '/admin/jobs',
  JOB_CREATE: '/admin/jobs/create',
  TASKS: '/admin/tasks',
  TEAMS: '/admin/teams',
  TEAM_VIEW: '/admin/teams/:id',
  JOB_DETAIL: '/admin/jobs/:id',
  TASK_DETAIL: '/admin/tasks/:id',
  PROFILE: '/admin/profile',
  // Public branding pages
  PRICING: '/pricing',
  ABOUT: '/about',
  CONTACT: '/contact',
  SETTINGS: '/admin/settings',
  COMPANY_SETTINGS: '/admin/company-settings',
  COMPANY_PROFILE: '/admin/companies/:id',
} as const;

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  SUPERVISOR: 'supervisor',
  WORKER: 'worker',
  CUSTOMER: 'customer',
} as const;

// Job Statuses
export const JOB_STATUSES = {
  ASSIGNED:       'assigned',
  IN_PROGRESS:    'in_progress',
  PENDING_REVIEW: 'pending_review',
  APPROVED:       'approved',
  REJECTED:       'rejected',
  ARCHIVED:       'archived',
  COMPLETED:      'completed', // legacy — kept for backward compatibility
} as const;

// Task Statuses
export const TASK_STATUSES = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
} as const;

// User Statuses
export const USER_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
} as const;

// Company Statuses
export const COMPANY_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
} as const;

// Team Statuses
export const TEAM_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

// Customer Statuses
export const CUSTOMER_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

// Image Types
export const IMAGE_TYPES = {
  BEFORE: 'before',
  AFTER: 'after',
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 15,
  PAGE_SIZE_OPTIONS: [10, 15, 25, 50, 100],
} as const;

// File Upload
export const FILE_UPLOAD = {
  MAX_IMAGE_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_PROFILE_IMAGE_SIZE: 2 * 1024 * 1024, // 2MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'],
  MAX_IMAGES_PER_UPLOAD: 10,
} as const;

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  DISPLAY_WITH_TIME: 'MMM dd, yyyy HH:mm',
  API: 'yyyy-MM-dd',
  API_WITH_TIME: 'yyyy-MM-dd HH:mm:ss',
} as const;

// Status Colors — aligned with theme palette (src/core/theme/theme.ts)
export const STATUS_COLORS = {
  [JOB_STATUSES.ASSIGNED]:       '#3B82F6',  // info.main
  [JOB_STATUSES.IN_PROGRESS]:    '#F97316',  // secondary.main
  [JOB_STATUSES.PENDING_REVIEW]: '#8B5CF6',  // purple
  [JOB_STATUSES.APPROVED]:       '#10B981',  // success.main
  [JOB_STATUSES.REJECTED]:       '#EF4444',  // error.main
  [JOB_STATUSES.ARCHIVED]:       '#6B7280',  // text.secondary
  [JOB_STATUSES.COMPLETED]:      '#22C55E',  // success.main (legacy)
  [TASK_STATUSES.PENDING]: '#f59e0b',      // warning.main
  [TASK_STATUSES.IN_PROGRESS]: '#F97316',  // secondary.main
  [TASK_STATUSES.COMPLETED]: '#22C55E',    // success.main
  [TASK_STATUSES.REJECTED]: '#EF4444',     // error.main
  [USER_STATUSES.ACTIVE]: '#22C55E',       // success.main
  [USER_STATUSES.INACTIVE]: '#6B7280',     // text.secondary
  [USER_STATUSES.SUSPENDED]: '#EF4444',    // error.main
  [COMPANY_STATUSES.ACTIVE]: '#22C55E',    // success.main
  [COMPANY_STATUSES.INACTIVE]: '#6B7280',  // text.secondary
  [COMPANY_STATUSES.SUSPENDED]: '#EF4444', // error.main
  [TEAM_STATUSES.ACTIVE]: '#22C55E',       // success.main
  [TEAM_STATUSES.INACTIVE]: '#6B7280',     // text.secondary
  [CUSTOMER_STATUSES.ACTIVE]: '#22C55E',   // success.main
  [CUSTOMER_STATUSES.INACTIVE]: '#6B7280', // text.secondary
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied.',
  NOT_FOUND: 'Resource not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  INVALID_CREDENTIALS: 'Invalid email or password.',
  TOKEN_EXPIRED: 'Your session has expired. Please login again.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful!',
  LOGOUT_SUCCESS: 'Logged out successfully!',
  SAVE_SUCCESS: 'Saved successfully!',
  DELETE_SUCCESS: 'Deleted successfully!',
  UPDATE_SUCCESS: 'Updated successfully!',
  CREATE_SUCCESS: 'Created successfully!',
  UPLOAD_SUCCESS: 'Uploaded successfully!',
} as const;
